import moment from 'moment';
import {
    splitCell, docName, weekEnding, isSummaryDoc, isSupplementDoc, isTestDocId,
    partDocId, partEnvelopes, stageAcross, summaryStage, weekCrewEnvelope,
} from './billingTicketDocs';

// Week tracker for the Ticket Map.
//
// A billing week (Monday to Sunday) moves through three things the client
// signs, in order: the crews' daily field tickets, the week summary over them,
// and the invoice that bills the week. The map answers "who owns this day";
// this answers "where does this week stand" — one row per week ending, read
// off the same ticket documents and DocuSign envelopes the map already has.
//
//   tickets  every (work date, shift) cell a ticket document covers in the
//            week, by where its CREW send stands — the summary coming back
//            does not sign a day ticket — plus work days no ticket covers yet
//   summary  the week summary: a summary document on its own (kind
//            'summary'), a ticket's own summary send, or a script-sent
//            envelope whose name says "Week Summary"
//   invoice  the invoice approval envelope ('Doc', Doc_ID 'inv<number>')
//            whose billing period overlaps the week. The period is read off
//            the document name ("… 9.7.26-9.20.26.pdf"); without one the
//            envelope speaks for the week of its work date only.
//
// Pure: no React, no fetches.

const DEAD = ['voided', 'expired', 'failed'];
const statusOf = (e) => String(e?.Status || '').toLowerCase();
const alive = (e) => Boolean(e) && !DEAD.includes(statusOf(e));
const STAGE_RANK = { Declined: 0, Draft: 1, Validated: 2, 'Sent for Signature': 3, Signed: 4 };

export const isSummaryEnvelope = (e) => /week summary/i.test(`${e?.Document_Name || ''} ${e?.Subject || ''}`);
export const isInvoiceEnvelope = (e) => e?.Shift === 'Doc'
    && /^inv[a-z0-9]+$/i.test(String(e.Doc_ID || '')) && !isTestDocId(e.Doc_ID);

/**
 * An invoice approved in more than one envelope reads as one. F00294315 went
 * to Ryan Bakke and to Daniel Ji in envelopes of their own (10/21-10/22/26),
 * and both are kept under its Doc_ID (billingSignature.js adoptEnvelope). The
 * live envelopes of one invoice fold into its first: every part's signers,
 * completed only once every part is, declined if any part is, `parts` the
 * envelopes themselves. Of two live copies of one request — envelopes sharing
 * a signer — only the later counts: Anna re-sent F00294234 to Ryan and Daniel
 * on 10/23 while Fusion's 9/30 send to Brad, Ryan and Daniel was still open.
 * Dead envelopes and everything that is not an invoice approval pass through
 * as they are, in place.
 */
const signerEmailsOf = (e) => {
    const list = (e.recipients || []).map((r) => r.Email);
    return (list.length ? list : String(e.Signer_Email || '').split(','))
        .map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);
};
const sentKey = (e) => `${String(e.Sent_At_Utc || '').replace('T', ' ').slice(0, 19)}|${String(e.RowID || 0).padStart(12, '0')}`;
const shareSigner = (a, b) => !a.length || !b.length || a.some((x) => b.includes(x));
const latestCopies = (parts) => parts.filter((e) => !parts.some((o) => o !== e
    && sentKey(o) > sentKey(e) && shareSigner(signerEmailsOf(o), signerEmailsOf(e))));

export const mergeInvoiceEnvelopes = (envelopes) => {
    const groups = {};
    const order = [];
    (envelopes || []).forEach((e) => {
        if (!isInvoiceEnvelope(e) || !alive(e)) { order.push(e); return; }
        const k = String(e.Doc_ID).toLowerCase();
        if (!groups[k]) { groups[k] = []; order.push(k); }
        groups[k].push(e);
    });
    const fold = (group) => {
        const parts = latestCopies(group);
        if (parts.length === 1) return parts[0];
        const sorted = parts.slice().sort((a, b) => Number(a.RowID || 0) - Number(b.RowID || 0));
        const st = parts.map(statusOf);
        const status = st.includes('declined') ? 'declined'
            : st.every((s) => s === 'completed') ? 'completed'
                : st.some((s) => ['sent', 'delivered', 'completed'].includes(s)) ? 'sent' : 'created';
        const times = (k) => parts.map((p) => p[k]).filter(Boolean).map(String).sort();
        return {
            ...sorted[0],
            Status: status,
            Sent_At_Utc: times('Sent_At_Utc')[0] || null,
            Completed_At_Utc: status === 'completed' ? times('Completed_At_Utc').pop() || null : null,
            Declined_At_Utc: status === 'declined' ? times('Declined_At_Utc').pop() || null : null,
            Signer_Name: parts.map((p) => p.Signer_Name).filter(Boolean).join(', '),
            Signer_Email: parts.map((p) => p.Signer_Email).filter(Boolean).join(', '),
            recipients: sorted.flatMap((p) => p.recipients || []),
            parts: sorted,
        };
    };
    return order.map((x) => (typeof x === 'string' ? fold(groups[x]) : x));
};

const monday = (sunday) => moment(sunday, 'YYYY-MM-DD').subtract(6, 'days').format('YYYY-MM-DD');

/** { from, to } off a document name carrying "… 9.7.26-9.20.26 …", else null. */
const namePeriod = (name) => {
    const m = /(\d{1,2})\.(\d{1,2})\.(\d{2})\s*-\s*(\d{1,2})\.(\d{1,2})\.(\d{2})/.exec(String(name || ''));
    if (!m) return null;
    const iso = (mo, d, y) => moment(`20${y}-${mo}-${d}`, 'YYYY-M-D');
    const from = iso(m[1], m[2], m[3]); const to = iso(m[4], m[5], m[6]);
    return from.isValid() && to.isValid() && !to.isBefore(from)
        ? { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') } : null;
};

/**
 * { no, from, to, named } for an invoice approval envelope: the period the
 * document's name states ("9.21.26-10.4.26", named true), else the work
 * date's week (named false: a mobilization or third-party approval carries
 * no period, and its week is only a guess).
 */
export const invoicePeriod = (e) => {
    const no = (/invoice\s+([a-z]*\d[a-z0-9]*)/i.exec(String(e?.Subject || '')) || [])[1]
        || String(e?.Doc_ID || '').slice(3);
    const p = namePeriod(e?.Document_Name);
    if (p) return { no: no.toUpperCase(), ...p, named: true };
    const we = weekEnding(e?.Work_Date);
    return { no: no.toUpperCase(), from: we ? monday(we) : '', to: we, named: false };
};

const invoiceStage = (e) => {
    const s = statusOf(e);
    if (s === 'completed') return 'Approved';
    if (s === 'declined') return 'Declined';
    if (s === 'created') return 'Draft';
    return 'Out for approval';
};

/** The envelope standing on one cell's crew send, leaving a summary envelope out of it. */
const crewEnvelope = (doc, d, sh, sigByDoc) => {
    const part = sh === 'Night' ? 'night' : 'day';
    const legacy = sigByDoc?.[doc.id];
    const own = sigByDoc?.[partDocId(doc.id, part)] || (legacy && !isSummaryEnvelope(legacy) ? legacy : null);
    if (alive(own)) return own;
    return weekCrewEnvelope(sigByDoc, weekEnding(d), sh) || own || null;
};

const cellStage = (doc, d, sh, sigByDoc) => {
    if (doc.st === 'Signed' && doc.signed) return 'Signed';
    const env = crewEnvelope(doc, d, sh, sigByDoc);
    return stageAcross(doc, [env], 1) || doc.st || 'Draft';
};

const ticketsOf = (cells, untracked) => {
    const n = { total: cells.length, signed: 0, out: 0, draft: 0, declined: 0, untracked: untracked.length };
    cells.forEach((c) => {
        if (c.stage === 'Signed') n.signed += 1;
        else if (c.stage === 'Sent for Signature') n.out += 1;
        else if (c.stage === 'Declined') n.declined += 1;
        else n.draft += 1;
    });
    let stage = 'Draft';
    if (!n.total) stage = 'None';
    else if (n.declined) stage = 'Declined';
    else if (n.signed === n.total && !n.untracked) stage = 'Signed';
    else if (n.out) stage = 'Out';
    else if (n.signed) stage = 'Partial';
    return { ...n, stage, untrackedDays: untracked };
};

const summaryOf = (weekDocs, inWeek, sigByDoc) => {
    const found = [];
    weekDocs.forEach((doc) => {
        const cells = doc.cells.filter(inWeek);
        if (isSummaryDoc(doc)) {
            const env = partEnvelopes(doc, sigByDoc).summary || null;
            found.push({ doc, env, stage: summaryStage(doc, sigByDoc), cells });
            return;
        }
        if (isSupplementDoc(doc)) return;
        const legacy = sigByDoc?.[doc.id];
        const env = sigByDoc?.[partDocId(doc.id, 'summary')] || (legacy && isSummaryEnvelope(legacy) ? legacy : null);
        if (!env) return;
        found.push({ doc, env, stage: stageAcross(doc, [env], 1) || 'Draft', cells });
    });
    if (!found.length) return null;
    found.sort((a, b) => (STAGE_RANK[b.stage] ?? 1) - (STAGE_RANK[a.stage] ?? 1)
        || String(b.env?.Sent_At_Utc || '').localeCompare(String(a.env?.Sent_At_Utc || '')));
    const { doc, env, stage, cells } = found[0];
    return {
        stage,
        docId: doc.id,
        own: isSummaryDoc(doc),
        name: docName(cells),
        signers: String(env?.Signer_Name || ''),
        at: stage === 'Signed' ? String(env?.Completed_At_Utc || doc.signed?.t || '') : String(env?.Sent_At_Utc || ''),
    };
};

const nextStep = (tickets, summary, invoices) => {
    const live = invoices.filter((i) => i.stage !== 'Declined');
    if (live.some((i) => i.stage === 'Approved')) return 'Invoiced and approved';
    if (live.length) return live[0].stage === 'Draft' ? 'Send the invoice for approval' : 'Invoice is out for approval';
    if (tickets.stage === 'None') return 'Build the daily tickets';
    if (tickets.stage === 'Declined') return 'A daily ticket was declined';
    if (tickets.stage !== 'Signed') {
        const open = tickets.draft + tickets.untracked;
        if (open) return `${open} day ticket${open === 1 ? '' : 's'} still to send`;
        return `${tickets.out} day ticket${tickets.out === 1 ? '' : 's'} out for signature`;
    }
    if (!summary || summary.stage === 'Draft' || summary.stage === 'Validated') return 'Send the week summary';
    if (summary.stage === 'Declined') return 'The week summary was declined';
    if (summary.stage !== 'Signed') return 'Week summary is out for signature';
    return 'Ready to invoice';
};

/**
 * One row per week ending, newest first.
 *   docs       live ticket documents (parseDocs)
 *   sigByDoc   envelopesByDoc(envelopes)
 *   envelopes  the job's envelope rows, for the invoice approvals
 *   days       work dates the job has ('YYYY-MM-DD'), to count days no ticket covers
 */
export const weekTracker = ({ docs = [], sigByDoc = {}, envelopes = [], days = [] } = {}) => {
    const live = (docs || []).filter((d) => d && !d.del && Array.isArray(d.cells));
    const weeks = new Set();
    live.forEach((doc) => doc.cells.forEach((k) => { const we = weekEnding(splitCell(k).d); if (we) weeks.add(we); }));
    (days || []).forEach((d) => { const we = weekEnding(d); if (we) weeks.add(we); });

    // First document to claim a cell keeps it, as on the map.
    const owner = {};
    live.filter((doc) => !isSummaryDoc(doc) && !isSupplementDoc(doc))
        .forEach((doc) => doc.cells.forEach((k) => { if (!owner[k]) owner[k] = doc; }));

    const invoices = mergeInvoiceEnvelopes(envelopes).filter((e) => isInvoiceEnvelope(e) && alive(e))
        .map((e) => ({ ...invoicePeriod(e), stage: invoiceStage(e), at: String(e.Completed_At_Utc || e.Sent_At_Utc || ''), rowId: e.RowID }))
        .sort((a, b) => Number(b.rowId || 0) - Number(a.rowId || 0));

    return [...weeks].sort((a, b) => b.localeCompare(a)).map((weekEnd) => {
        const from = monday(weekEnd);
        const inWeek = (k) => { const { d } = splitCell(k); return d >= from && d <= weekEnd; };
        const cells = Object.keys(owner).filter(inWeek).sort().map((k) => {
            const { d, sh } = splitCell(k);
            return { cell: k, docId: owner[k].id, stage: cellStage(owner[k], d, sh, sigByDoc) };
        });
        const covered = new Set(cells.map((c) => splitCell(c.cell).d));
        const untracked = [...new Set((days || []).filter((d) => d >= from && d <= weekEnd && !covered.has(d)))].sort();
        const tickets = ticketsOf(cells, untracked);
        const summary = summaryOf(live.filter((doc) => doc.cells.some(inWeek)), inWeek, sigByDoc);
        const inv = invoices.filter((i) => i.from && i.to && i.from <= weekEnd && i.to >= from);
        return { weekEnd, from, tickets, summary, invoices: inv, next: nextStep(tickets, summary, inv) };
    });
};

// ── The day list's week summary and invoice ──
//
// The Daily Field Tickets day list carries two more columns beside each day's
// Day / Night sheets: the week summary over the day and the invoice billing
// it. Both are read off what actually went out — the summary envelopes and
// the invoice approvals — so a summary sent by script shows whether or not
// its document was ever synced into the map; and an invoice also off the
// Billing tab's own summaries, so one not yet routed for approval still shows.

const isDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ''));
const SENT_RANK = { completed: 3, sent: 2, delivered: 2, declined: 1 };

/** The days a summary envelope covers: its name's period, else its document's cells, else its work date's week. */
const summarySpan = (e, docById) => {
    const p = namePeriod(e.Document_Name);
    if (p) return p;
    const id = String(e.Doc_ID || '');
    const doc = docById[id] || (id.endsWith('s') ? docById[id.slice(0, -1)] : null);
    const dates = (doc?.cells || []).map((k) => splitCell(k).d).filter(isDate).sort();
    if (dates.length) return { from: dates[0], to: dates[dates.length - 1] };
    const we = weekEnding(e.Work_Date);
    return we ? { from: monday(we), to: we } : null;
};

/**
 * The job's invoices and the days each bills, newest first. A billing
 * summary that is not void is dated by its FTMS period, else by the work
 * dates of the labor on it; an approval envelope joins its summary by number
 * and dates it from its own name when the summary has no dates of its own.
 * An approval with no period in its name and no summary behind it (the PB4
 * mobilization) bills no work day, so it stays off the day list.
 */
const invoiceSpans = (envelopes, invoices, billed) => {
    const key = (no) => String(no || '').trim().toUpperCase();
    const byNo = {};
    (invoices || []).filter((v) => v && v.no && v.status !== 'Void').forEach((v) => {
        const f = v.ftms || {};
        const ftms = isDate(f.from) && isDate(f.to) && f.from <= f.to;
        byNo[key(v.no)] = { no: key(v.no), status: v.status || 'Draft', from: ftms ? f.from : '', to: ftms ? f.to : '', fixed: ftms, env: null };
    });
    Object.values(billed || {}).forEach((b) => {
        const v = byNo[key(b?.inv)];
        const d = String(b?.snap?.d || '').slice(0, 10);
        if (!v || v.fixed || !/^51/.test(String(b.snap.ct || '')) || !isDate(d)) return;
        if (!v.from || d < v.from) v.from = d;
        if (!v.to || d > v.to) v.to = d;
    });
    mergeInvoiceEnvelopes(envelopes).filter((e) => isInvoiceEnvelope(e) && alive(e))
        .sort((a, b) => Number(b.RowID || 0) - Number(a.RowID || 0))
        .forEach((e) => {
            const no = key(invoicePeriod(e).no);
            const v = byNo[no] || (byNo[no] = { no, status: '', from: '', to: '', env: null });
            if (!v.env) v.env = e;
            const p = !v.from ? namePeriod(e.Document_Name) : null;
            if (p) { v.from = p.from; v.to = p.to; }
        });
    return Object.values(byNo).filter((v) => v.from && v.to)
        .map(({ fixed, ...v }) => v)
        .sort((a, b) => b.to.localeCompare(a.to) || b.no.localeCompare(a.no));
};

/**
 * Lump-sum invoices: approvals with no billing period in their name and no
 * Billing summary behind them (the PB4 mobilization). They bill no work day,
 * so the day list and the periods leave them out; this lists them on their
 * own, newest first, as [{ no, what, env }] — `what` the name's own words
 * ("PB4 Mobilization").
 */
export const lumpInvoices = ({ envelopes = [], invoices = [] } = {}) => {
    const key = (no) => String(no || '').trim().toUpperCase();
    const onSummary = new Set((invoices || []).filter((v) => v && v.no && v.status !== 'Void').map((v) => key(v.no)));
    const out = {};
    mergeInvoiceEnvelopes(envelopes).filter((e) => isInvoiceEnvelope(e) && alive(e) && !namePeriod(e.Document_Name))
        .sort((a, b) => Number(b.RowID || 0) - Number(a.RowID || 0))
        .forEach((e) => {
            const no = key(invoicePeriod(e).no);
            if (onSummary.has(no) || out[no]) return;
            const what = String(e.Document_Name || '').replace(/\.pdf$/i, '').replace(/^invoice\s+\S+\s+approval\s*[-–—]?\s*/i, '').trim();
            out[no] = { no, what, env: e };
        });
    return Object.values(out);
};

/**
 * (date) => { summary, invoices } for the day list.
 *   summary   { env, from, to, docId } — the summary envelope that went out
 *             over the date (signed before out before declined, then
 *             newest), or null. A draft that never left, a dead send and a
 *             test send do not count.
 *   invoices  [{ no, status, from, to, env }] billing the date, newest first;
 *             `status` is the Billing summary's ('' when only the approval
 *             envelope is known), `env` its approval envelope or null.
 *
 *   envelopes  the job's envelope rows
 *   docs       ticket documents — only to date a summary whose name has no period
 *   invoices   the Billing tab's summaries (parseBillingRows .invoices)
 *   billed     parseBillingRows .billed — the line snapshots, to date a summary FTMS has not
 */
export const dayBilling = ({ envelopes = [], docs = [], invoices = [], billed = {} } = {}) => {
    const docById = {};
    (docs || []).forEach((doc) => { if (doc && doc.id && !doc.del) docById[doc.id] = doc; });
    const summaries = (envelopes || [])
        .filter((e) => isSummaryEnvelope(e) && !isInvoiceEnvelope(e) && !isTestDocId(e.Doc_ID) && SENT_RANK[statusOf(e)])
        .map((env) => {
            const span = summarySpan(env, docById);
            const id = String(env.Doc_ID || '');
            const doc = docById[id] || (id.endsWith('s') ? docById[id.slice(0, -1)] : null);
            return span ? { env, ...span, docId: doc ? doc.id : null } : null;
        })
        .filter(Boolean)
        .sort((a, b) => SENT_RANK[statusOf(b.env)] - SENT_RANK[statusOf(a.env)]
            || String(b.env.Sent_At_Utc || '').localeCompare(String(a.env.Sent_At_Utc || ''))
            || Number(b.env.RowID || 0) - Number(a.env.RowID || 0));
    const invs = invoiceSpans(envelopes, invoices, billed);
    return (d) => ({
        summary: summaries.find((s) => s.from <= d && s.to >= d) || null,
        invoices: invs.filter((v) => v.from <= d && v.to >= d),
    });
};

/**
 * Where one invoice stands, in a word or two, and its tone: 'done' (approved
 * or invoiced), 'out' (with the client), 'bad' (declined or kicked back) or
 * 'draft'. `progress` is signerProgress, passed in to keep this file off the
 * envelope helpers' recipient parsing.
 */
export const invoiceStatus = (v, progress) => {
    const s = statusOf(v?.env);
    // The summary's own stage leads where it is past the approval or has
    // been sent back since; otherwise the approval says more than "in FTMS".
    if (v?.status === 'Invoiced') return { label: 'invoiced', tone: 'done' };
    if (v?.status === 'Kicked Back') return { label: 'kicked back', tone: 'bad' };
    if (s === 'completed') return { label: 'approved', tone: 'done' };
    if (s === 'declined') return { label: 'declined', tone: 'bad' };
    if (s === 'sent' || s === 'delivered') {
        const p = progress ? progress(v.env) : { total: 0, signed: 0 };
        return { label: p.total > 1 && p.signed ? `${p.signed}/${p.total} approved` : 'out for approval', tone: 'out' };
    }
    if (v?.status === 'Uploaded to FTMS') return { label: 'in FTMS', tone: 'out' };
    return { label: 'draft', tone: 'draft' };
};

// ── Billing outlook ──
//
// The header over the Daily Field Tickets day list: the next 14 days with
// the billing dates that fall in them, and where the job's billing stands —
// periods still to bill, invoices in flight and their payment due dates, and
// the third-party vendor invoices the next client invoice is waiting on.
//
// Billing periods are read off the invoices themselves: each dated invoice
// (FTMS period, else the labor on it, else its approval's name) closes a
// period, the next period opens the day after the latest one, and periods
// run at the job's own cadence — the median length of its dated invoices,
// 14 days until it has any. A period is billed BILL_GRACE days after it
// ends (the 9/7–9/20 draft went up 9/24); past that it reads overdue.
//
// Payment due is the invoice date plus its terms ('Net 30'). The app reads
// nothing from JDE accounts receivable, so "past due" here means the terms
// have run out, not that the client has not paid.

export const BILL_GRACE = 7;
export const OUTLOOK_DAYS = 14;
const OPEN_INV = ['Draft', 'Kicked Back', 'Uploaded to FTMS'];
const addDays = (d, n) => moment(d, 'YYYY-MM-DD').add(n, 'days').format('YYYY-MM-DD');
const mdOf = (d) => moment(d, 'YYYY-MM-DD').format('M/D');
const median = (xs) => {
    const s = [...xs].sort((a, b) => a - b);
    return s.length ? s[Math.floor(s.length / 2)] : 0;
};

/** Days in 'Net 30' (and the like); 30 when the terms say nothing. */
export const termsDays = (terms) => {
    const m = /(\d+)/.exec(String(terms || ''));
    return m ? Number(m[1]) : 30;
};

/** The date an invoice went to the client: its Invoiced / FTMS stamp, else its date. */
const invoicedOn = (v) => {
    const st = (v.h || []).filter((x) => x && ['Invoiced', 'Paid', 'Uploaded to FTMS', 'Sent to Client'].includes(x.s));
    const t = st.length ? String(st[st.length - 1].t || '').slice(0, 10) : '';
    if (isDate(t)) return t;
    const d = String(v.date || '').slice(0, 10);
    return isDate(d) ? d : '';
};

/**
 *   today      'YYYY-MM-DD'
 *   envelopes  the job's envelope rows
 *   docs       ticket documents
 *   invoices   the Billing tab's summaries (parseBillingRows .invoices)
 *   billed     parseBillingRows .billed
 *   terms      the job's payment terms ('Net 30')
 *   days       work dates the job has ('YYYY-MM-DD')
 *   weeks      weekTracker rows, for where each period's paper stands
 *   progress   signerProgress, for invoiceStatus
 *   thirdParty { waiting: { n, amt, oldest }, ready: { n, amt, oldest } } or null
 */
export const billingOutlook = ({
    today, envelopes = [], docs = [], invoices = [], billed = {}, terms = 'Net 30', days = [], weeks = [], progress = null, thirdParty = null,
} = {}) => {
    const t0 = isDate(today) ? today : moment().format('YYYY-MM-DD');
    const net = termsDays(terms);
    const spans = invoiceSpans(envelopes, invoices, billed);
    const spanByNo = {};
    spans.forEach((s) => { spanByNo[s.no] = s; });

    // Every live invoice, newest first: its stage, period and payment due.
    const live = (invoices || []).filter((v) => v && v.no && v.status !== 'Void').map((v) => {
        const sp = spanByNo[String(v.no).trim().toUpperCase()] || null;
        const info = invoiceStatus({ ...v, env: sp?.env || null }, progress);
        const sent = invoicedOn(v);
        const invoiced = v.status === 'Invoiced' || v.status === 'Paid';
        const due = invoiced && sent ? addDays(sent, net) : '';
        return {
            no: String(v.no), status: v.status, label: info.label, tone: info.tone,
            from: sp?.from || '', to: sp?.to || '', env: sp?.env || null,
            sent, due, pastDue: Boolean(due && due < t0), open: OPEN_INV.includes(v.status) || !invoiced,
        };
    });
    // Approvals with no summary behind them still bill: dated ones by their
    // name's period, lump sums (the PB4 mobilization) with no period at all.
    spans.filter((s) => !live.some((v) => v.no === s.no)).forEach((s) => {
        const info = invoiceStatus(s, progress);
        live.push({ no: s.no, status: '', label: info.label, tone: info.tone, from: s.from, to: s.to, env: s.env, sent: '', due: '', pastDue: false, open: info.tone !== 'done' });
    });
    lumpInvoices({ envelopes, invoices }).filter((s) => !live.some((v) => v.no === s.no)).forEach((s) => {
        const info = invoiceStatus(s, progress);
        live.push({ no: s.no, what: s.what, status: '', label: info.label, tone: info.tone, from: '', to: '', env: s.env, sent: '', due: '', pastDue: false, open: info.tone !== 'done' });
    });
    live.sort((a, b) => String(b.to || b.sent).localeCompare(String(a.to || a.sent)) || b.no.localeCompare(a.no));

    // Billing periods: closed by the dated invoices, then rolling forward at
    // the job's cadence from the day after the latest one.
    const dated = spans.filter((s) => s.from && s.to);
    const cadence = median(dated.map((s) => moment(s.to, 'YYYY-MM-DD').diff(moment(s.from, 'YYYY-MM-DD'), 'days') + 1)) || OUTLOOK_DAYS;
    const work = [...new Set((days || []).filter(isDate))].sort();
    const horizonEnd = addDays(t0, OUTLOOK_DAYS - 1);
    const lastTo = dated.reduce((m, s) => (s.to > m ? s.to : m), '');
    let from = lastTo ? addDays(lastTo, 1) : (work[0] || '');
    const periods = [];
    while (from && from <= horizonEnd && periods.length < 8) {
        const to = addDays(from, cadence - 1);
        const billBy = addDays(to, BILL_GRACE);
        const kind = to < t0 ? (billBy < t0 ? 'overdue' : 'due') : from <= t0 ? 'current' : 'next';
        const inPeriod = work.filter((d) => d >= from && d <= to);
        // Where the period's paper stands, off the week tracker.
        const wk = (weeks || []).filter((w) => w.weekEnd >= from && w.from <= to);
        const unsigned = wk.reduce((a, w) => a + (w.tickets.total - w.tickets.signed) + w.tickets.untracked, 0);
        const summariesOpen = wk.filter((w) => w.tickets.total && (!w.summary || w.summary.stage !== 'Signed')).length;
        // One period up next is enough; the ones past it say nothing yet.
        if (kind === 'next' && periods.some((p) => p.kind === 'next')) break;
        periods.push({ from, to, billBy, kind, workDays: inPeriod.length, unsigned, summariesOpen, weeks: wk.length });
        from = addDays(to, 1);
    }

    // The next 14 days, with the dates that fall on each.
    const horizon = [];
    for (let i = 0; i < OUTLOOK_DAYS; i += 1) {
        const d = addDays(t0, i);
        const marks = [];
        if (moment(d, 'YYYY-MM-DD').day() === 0) marks.push({ k: 'we', text: 'Week ends — send the week summary' });
        periods.forEach((p) => {
            if (p.to === d) marks.push({ k: 'period', text: `Billing period ${mdOf(p.from)}–${mdOf(p.to)} ends` });
            if (p.billBy === d && p.kind !== 'next') marks.push({ k: 'bill', text: `Bill ${mdOf(p.from)}–${mdOf(p.to)} by today` });
        });
        live.forEach((v) => { if (v.due === d) marks.push({ k: 'pay', text: `${v.no} payment due (${terms || `Net ${net}`})` }); });
        horizon.push({ d, marks, today: i === 0 });
    }

    const current = periods.find((p) => p.kind === 'current') || null;
    const toBill = periods.filter((p) => p.kind === 'overdue' || p.kind === 'due');
    return {
        today: t0, cadence, net, horizon, periods, toBill, current,
        invoices: live,
        pastDue: live.filter((v) => v.pastDue),
        inFlight: live.filter((v) => v.open && !v.pastDue),
        // Vendor invoices: what the next client invoice waits on, and by when.
        thirdParty: thirdParty ? { ...thirdParty, neededBy: (toBill[0] || current)?.billBy || '' } : null,
    };
};
